// Copied from platform-app components/pos/LayoutEditor.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Save, Pin, DollarSign, LayoutGrid, Grid3x3, Percent, Monitor, Palette, PanelLeft, PanelTop, Sun, Moon, ShieldCheck } from 'lucide-react';
import PosThemeTab, { type PosThemeValue, type PosThemeScope } from './PosThemeTab';
import { BUTTON_REGISTRY, HEADER_BUTTON_REGISTRY, normalizeHeaderButtons, type ButtonOverride, type HeaderButtonConfig } from '../../config/buttonRegistry';

const FIXED_BUTTON_IDS = ['custom-item'];
/** POS Defaults: the tax/fee picker is tenant data — hidden on the global default. */
const SHOW_TAX_FEE_PICKER = false;
import { getIcon as getIconFromRegistry } from '../../utils/iconRegistry';
import KeypadDesigner from './KeypadDesigner';
import ButtonCustomizerPopup from './ButtonCustomizerPopup';
import RailPreview from './layout/RailPreview';
import HeaderPreview from './layout/HeaderPreview';
import MoreLauncherPreview from './layout/MoreLauncherPreview';
import DiscountPopupPreview from './layout/DiscountPopupPreview';
import type { PreviewTheme } from './layout/previewPalette';
import { isAssignableAction, resolveAddKeyLook } from './layout/addKeyLook';
import ButtonAuthorizationTab from './layout/ButtonAuthorizationTab';
import { taxesFeesApi } from '../../services/posApiService';
import CustomerFacingTab from './customerFacing/CustomerFacingTab';
import { defaultCustomerDisplayConfig } from './customerFacing/defaults';

import { RubyGemMark } from '../RubyLoader';
interface TaxFeeItem {
  taxFeeId: string;
  name: string;
  type: 'tax' | 'fee';
  rateType: 'percentage' | 'fixed';
  rateValue: number;
}

export interface LayoutEditorProps {
  availableIds: string[];
  initialPinned: string[];
  initialKeypadSlots: Record<string, { action: string; label?: string } | null>;
  initialCustomizations: Record<string, ButtonOverride>;
  /** POS Header buttons (Scan / Hold / Open orders). Absent = today's header. */
  initialHeaderButtons?: HeaderButtonConfig[] | null;
  onSave: (layout: {
    pinned: string[];
    keypadSlots: Record<string, { action: string; label?: string } | null>;
    buttonCustomizations: Record<string, ButtonOverride>;
    headerButtons: HeaderButtonConfig[];
  }) => Promise<void>;
  saving: boolean;
  saveLabel?: string;
  customerDisplayConfig?: import('@platform/shared-types').APosCustomerDisplayConfig;
  terminalType?: string;
  onSaveCustomerDisplay?: (config: import('@platform/shared-types').APosCustomerDisplayConfig) => Promise<void>;
  savingCustomerDisplay?: boolean;
  /** Per-terminal cashier theme. Tab only appears where a save handler is wired. */
  posTheme?: PosThemeValue;
  onSaveTheme?: (theme: PosThemeValue, scope: PosThemeScope) => Promise<void>;
  savingTheme?: boolean;
  otherTerminalsExist?: boolean;
  /** 'template' when the theme is stored on a layout template, not a terminal. */
  themeVariant?: 'terminal' | 'template';
}

/**
 * Light/Dark switch shared by every tab with a POS preview. On a terminal's own page
 * (`saves`) it IS the terminal's POS theme and is saved with Save Layout; on the shared
 * template editor there is no terminal, so it only changes the preview.
 */
function PreviewThemeSwitch({ value, onChange, saves = false }: { value: PreviewTheme; onChange: (t: PreviewTheme) => void; saves?: boolean }) {
  return (
    <div className="flex items-center gap-1 rounded-[10px] border border-stone-200 bg-stone-100 p-1" role="group" aria-label={saves ? 'POS theme' : 'Preview theme'}>
      <span className="pl-1 pr-1.5 text-xs font-semibold text-stone-500">{saves ? 'POS theme' : 'Preview'}</span>
      {([
        { value: 'light' as const, label: 'Light', Icon: Sun },
        { value: 'dark' as const, label: 'Dark', Icon: Moon },
      ]).map(({ value: v, label, Icon }) => {
        const on = value === v;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(v)}
            className={`flex h-[30px] items-center gap-1.5 rounded-[7px] px-3 text-[13px] font-semibold transition-colors ${
              on ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <Icon size={14} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

const LayoutEditor: React.FC<LayoutEditorProps> = ({
  availableIds,
  initialPinned,
  initialKeypadSlots,
  initialCustomizations,
  initialHeaderButtons,
  onSave,
  saving,
  saveLabel = 'Save Layout',
  customerDisplayConfig,
  terminalType,
  onSaveCustomerDisplay,
  savingCustomerDisplay,
  posTheme,
  onSaveTheme,
  savingTheme,
  otherTerminalsExist,
  themeVariant = 'terminal',
}) => {
  // State — lifted from PosTerminalDetail
  const [localPinned, setLocalPinned] = useState<string[]>(initialPinned);
  const [localKeypadSlots, setLocalKeypadSlots] = useState(initialKeypadSlots);
  const [localCustomizations, setLocalCustomizations] = useState(initialCustomizations);
  const [customizerButton, setCustomizerButton] = useState<string | null>(null);
  // POS Header — always one entry per header id, in display order.
  const normalizedInitialHeader = useMemo(() => normalizeHeaderButtons(initialHeaderButtons), [JSON.stringify(initialHeaderButtons ?? null)]); // eslint-disable-line react-hooks/exhaustive-deps
  const [localHeaderButtons, setLocalHeaderButtons] = useState<HeaderButtonConfig[]>(normalizedInitialHeader);
  const [headerCustomizer, setHeaderCustomizer] = useState<HeaderButtonConfig['id'] | null>(null);
  const [customizerPreviewContext, setCustomizerPreviewContext] = useState<'action-bar' | 'keypad' | 'more-popup'>('action-bar');

  type LayoutTab = 'actionbar' | 'header' | 'keypad' | 'authorization' | 'custom' | 'empdisc' | 'customerfacing' | 'postheme';
  const [activeTab, setActiveTab] = useState<LayoutTab>('actionbar');
  // Light/Dark shown in the previews. On a terminal's page (onSaveTheme wired) this is the
  // terminal's POS theme and a change is saved by Save Layout; on the template editor it is preview-only.
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>(posTheme ?? 'light');
  // Follow the terminal's theme if it arrives after mount (preview state only).
  useEffect(() => { if (posTheme) setPreviewTheme(posTheme); }, [posTheme]);

  // Sync when the saved layout actually changes (e.g. after save, parent refetches).
  // Keyed on content, not identity: parents pass `x || []` / `x || {}`, which is a new
  // object on every parent render (e.g. a status poll) and would wipe unsaved edits.
  const initialPinnedKey = JSON.stringify(initialPinned);
  const initialKeypadSlotsKey = JSON.stringify(initialKeypadSlots);
  const initialCustomizationsKey = JSON.stringify(initialCustomizations);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocalPinned(initialPinned); }, [initialPinnedKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocalKeypadSlots(initialKeypadSlots); }, [initialKeypadSlotsKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLocalCustomizations(initialCustomizations); }, [initialCustomizationsKey]);
  useEffect(() => { setLocalHeaderButtons(normalizedInitialHeader); }, [normalizedInitialHeader]);

  // Custom Item Settings — taxes/fees lists
  const [taxList, setTaxList] = useState<TaxFeeItem[]>([]);
  const [feeList, setFeeList] = useState<TaxFeeItem[]>([]);
  const [taxFeesLoading, setTaxFeesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTaxFeesLoading(true);
    Promise.all([
      taxesFeesApi.list({ type: 'tax' }).catch(() => ({ taxesFees: [] })),
      taxesFeesApi.list({ type: 'fee' }).catch(() => ({ taxesFees: [] })),
    ]).then(([taxRes, feeRes]: [any, any]) => {
      if (cancelled) return;
      setTaxList((taxRes.taxesFees || []) as TaxFeeItem[]);
      setFeeList((feeRes.taxesFees || []) as TaxFeeItem[]);
      setTaxFeesLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Derived custom-item settings from localCustomizations
  const ciCustomItem = localCustomizations['custom-item']?.customItem ?? {};
  const ciAvailableTaxIds: string[] = (ciCustomItem as any).availableTaxIds ?? [];
  const ciAvailableFeeIds: string[] = (ciCustomItem as any).availableFeeIds ?? [];
  const ciDefaultTaxIds: string[] = (ciCustomItem as any).defaultTaxIds ?? [];
  const ciDefaultFeeIds: string[] = (ciCustomItem as any).defaultFeeIds ?? [];
  const ciLoyaltyEarnsPoints: boolean = (ciCustomItem as any).loyaltyEarnsPoints ?? false;

  // Derived employee-discount settings (always-visible card — not tied to action-bar layout)
  const edEnabled: boolean =
    localCustomizations['employee-discount']?.employeeDiscount?.enabled ?? false;
  const edPercentage: number | '' =
    (localCustomizations['employee-discount']?.employeeDiscount?.percentage ?? '') as number | '';

  const updateEdSettings = useCallback((patch: { enabled?: boolean; percentage?: number | '' }) => {
    setLocalCustomizations((prev) => {
      const existing = prev['employee-discount']?.employeeDiscount ?? { percentage: 0 };
      return {
        ...prev,
        'employee-discount': {
          ...prev['employee-discount'],
          employeeDiscount: {
            ...existing,
            ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
            ...(patch.percentage !== undefined ? { percentage: patch.percentage === '' ? 0 : patch.percentage } : {}),
          },
        },
      };
    });
  }, []);

  const updateCiCustomItem = useCallback((patch: Record<string, any>) => {
    setLocalCustomizations((prev) => ({
      ...prev,
      'custom-item': {
        ...prev['custom-item'],
        customItem: { ...(prev['custom-item']?.customItem ?? {}), ...patch },
      },
    }));
  }, []);

  const toggleCiShow = useCallback((kind: 'tax' | 'fee', id: string) => {
    setLocalCustomizations((prev) => {
      const ci = (prev['custom-item']?.customItem ?? {}) as any;
      const availIds: string[] = kind === 'tax' ? (ci.availableTaxIds ?? []) : (ci.availableFeeIds ?? []);
      const defIds: string[] = kind === 'tax' ? (ci.defaultTaxIds ?? []) : (ci.defaultFeeIds ?? []);
      let newAvail: string[], newDef: string[];
      if (availIds.includes(id)) {
        newAvail = availIds.filter((x: string) => x !== id);
        newDef = defIds.filter((x: string) => x !== id);
      } else {
        newAvail = [...availIds, id];
        newDef = defIds;
      }
      const patch = kind === 'tax'
        ? { availableTaxIds: newAvail, defaultTaxIds: newDef }
        : { availableFeeIds: newAvail, defaultFeeIds: newDef };
      return { ...prev, 'custom-item': { ...prev['custom-item'], customItem: { ...ci, ...patch } } };
    });
  }, []);

  const toggleCiDefault = useCallback((kind: 'tax' | 'fee', id: string) => {
    setLocalCustomizations((prev) => {
      const ci = (prev['custom-item']?.customItem ?? {}) as any;
      const availIds: string[] = kind === 'tax' ? (ci.availableTaxIds ?? []) : (ci.availableFeeIds ?? []);
      if (!availIds.includes(id)) return prev;
      const defIds: string[] = kind === 'tax' ? (ci.defaultTaxIds ?? []) : (ci.defaultFeeIds ?? []);
      const newDef = defIds.includes(id) ? defIds.filter((x: string) => x !== id) : [...defIds, id];
      const patch = kind === 'tax' ? { defaultTaxIds: newDef } : { defaultFeeIds: newDef };
      return { ...prev, 'custom-item': { ...prev['custom-item'], customItem: { ...ci, ...patch } } };
    });
  }, []);

  // Derived values. The Custom Item button is the keypad ADD key only — never on the
  // rail or in the More launcher, even if saved data lists it there.
  const railAvailableIds = availableIds.filter(isAssignableAction);
  const deactivatedIds = railAvailableIds.filter((id) => localCustomizations[id]?.active === false);
  const activeAvailableIds = railAvailableIds.filter((id) => localCustomizations[id]?.active !== false);
  const activePinned = localPinned.filter((id) => isAssignableAction(id) && localCustomizations[id]?.active !== false);
  // Dirty detection
  const layoutDirty = useMemo(() => {
    if (JSON.stringify(localPinned) !== JSON.stringify(initialPinned)) return true;
    if (JSON.stringify(localKeypadSlots) !== JSON.stringify(initialKeypadSlots)) return true;
    if (JSON.stringify(localCustomizations) !== JSON.stringify(initialCustomizations)) return true;
    if (JSON.stringify(localHeaderButtons) !== JSON.stringify(normalizedInitialHeader)) return true;
    return false;
  }, [localPinned, localKeypadSlots, localCustomizations, initialPinned, initialKeypadSlots, initialCustomizations, localHeaderButtons, normalizedInitialHeader]);
  // On a terminal's page the Light/Dark switch is the saved POS theme, so changing it
  // alone is a saveable change (no button edits needed).
  const themeDirty = !!onSaveTheme && previewTheme !== (posTheme ?? 'light');
  const isDirty = layoutDirty || themeDirty;
  const anySaving = !!saving || !!savingTheme;

  const handleSave = useCallback(async () => {
    if (layoutDirty) {
      await onSave({
        // Custom Item never belongs on the rail — drop it if older data pinned it.
        pinned: localPinned.filter(isAssignableAction),
        keypadSlots: localKeypadSlots,
        buttonCustomizations: localCustomizations,
        headerButtons: localHeaderButtons,
      });
    }
    if (themeDirty && onSaveTheme) {
      await onSaveTheme(previewTheme, 'terminal');
    }
  }, [layoutDirty, themeDirty, localPinned, localKeypadSlots, localCustomizations, localHeaderButtons, onSave, onSaveTheme, previewTheme]);

  // POS Header: the popup returns the same override shape as a rail button;
  // it replaces this entry's look/permissions and keeps its id and position.
  const handleHeaderCustomizerApply = useCallback((override: ButtonOverride) => {
    if (!headerCustomizer) return;
    const { customItem: _ci, employeeDiscount: _ed, ...rest } = override;
    setLocalHeaderButtons((prev) => prev.map((b) => (b.id === headerCustomizer ? { id: b.id, ...rest } : b)));
    setHeaderCustomizer(null);
  }, [headerCustomizer]);

  const moveHeaderButton = useCallback((id: HeaderButtonConfig['id'], dir: -1 | 1) => {
    setLocalHeaderButtons((prev) => {
      const i = prev.findIndex((b) => b.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, []);

  const handleCustomizerApply = useCallback((override: ButtonOverride) => {
    if (!customizerButton) return;
    setLocalCustomizations((prev) => {
      const existing = prev[customizerButton] ?? {};
      const merged: ButtonOverride = { ...override };
      if ((existing as any).customItem !== undefined) merged.customItem = (existing as any).customItem;
      if ((existing as any).employeeDiscount !== undefined) merged.employeeDiscount = (existing as any).employeeDiscount;
      return { ...prev, [customizerButton]: merged };
    });
    setCustomizerButton(null);
  }, [customizerButton]);

  return (
    <div className="space-y-4">
      {/* Inner sub-tabs */}
      <div className="flex flex-wrap gap-2">
        {([
          { id: 'actionbar', label: 'Side Rail', Icon: PanelLeft },
          { id: 'header', label: 'POS Header', Icon: PanelTop },
          { id: 'keypad', label: 'Keypad', Icon: Grid3x3 },
          { id: 'authorization', label: 'Button Authorization', Icon: ShieldCheck },
          { id: 'custom', label: 'Custom Item', Icon: DollarSign },
          { id: 'empdisc', label: 'Employee Discount', Icon: Percent },
          // Customer Facing Screen is per-terminal (publishes to a register), so it
          // only appears where a save handler is wired (PosTerminalDetail) — not in
          // the shared layout-template editor.
          ...(onSaveCustomerDisplay
            ? [{ id: 'customerfacing' as const, label: 'Customer Facing Screen', Icon: Monitor }]
            : []),
          // POS Theme is per-terminal too (publishes to a register), so it only
          // appears where a save handler is wired (PosTerminalDetail).
          ...(onSaveTheme
            ? [{ id: 'postheme' as const, label: 'POS Theme', Icon: Palette }]
            : []),
        ] as { id: LayoutTab; label: string; Icon: typeof LayoutGrid }[]).map(({ id, label, Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                active
                  ? 'bg-accent-600 text-white border-accent-600 shadow-sm'
                  : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
        {/* Always-visible Save for the layout tabs (the sticky bar below can sit off-screen
            on tall tabs). Customer Facing Screen and POS Theme keep their own Save. */}
        {activeTab !== 'customerfacing' && activeTab !== 'postheme' && (
          <button
            type="button"
            data-testid="layout-save-top"
            onClick={handleSave}
            disabled={!isDirty || anySaving}
            className="ml-auto flex items-center gap-2 px-5 py-2 min-h-[44px] rounded-lg bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={isDirty ? 'Save layout changes' : 'No unsaved changes'}
          >
            {anySaving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
            {saveLabel}
          </button>
        )}
      </div>

      {/* Button Authorization — who may use each button, and which need a manager PIN */}
      {activeTab === 'authorization' && (
        <ButtonAuthorizationTab
          buttonIds={[...availableIds, 'custom-item']}
          customizations={localCustomizations}
          onChange={setLocalCustomizations}
        />
      )}

      {/* Side Rail Layout card (tab id stays 'actionbar') */}
      {activeTab === 'actionbar' && (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 mb-4">
          <h3 className="text-stone-800 text-sm font-semibold">Side Rail Layout</h3>
          <span className="text-xs text-stone-400">Pinned order runs top to bottom on the till</span>
          <div className="flex-1" />
          {/* Preview-only theme switch — does not change the till's theme (see POS Theme tab) */}
          <PreviewThemeSwitch value={previewTheme} onChange={setPreviewTheme} saves={!!onSaveTheme} />
        </div>

        {railAvailableIds.length === 0 ? (
          <div className="py-4 px-3 rounded-lg bg-stone-50 border border-stone-100">
            <p className="text-stone-500 text-sm text-center">No buttons assigned by administrator</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-start gap-7 xl:grid-cols-[430px_1fr]">
            {/* Pinned — faithful side rail on a sale-screen silhouette */}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Pin size={12} className="text-accent-600" />
                <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">On the Rail</span>
                <span className="text-[10px] text-stone-400 ml-1">(drag to reorder · ✕ to unpin)</span>
              </div>
              <RailPreview
                theme={previewTheme}
                pinnedIds={activePinned}
                customizations={localCustomizations}
                onUnpin={(id) => setLocalPinned((prev) => prev.filter((p) => p !== id))}
                onReorder={(fromId, toIndex) => {
                  setLocalPinned((prev) => {
                    // The rail preview never shows custom-item (keypad-only), so index against
                    // the same filtered list it renders — legacy data may still contain it.
                    const current = prev.filter(isAssignableAction);
                    const oldIndex = current.indexOf(fromId);
                    if (oldIndex === -1) return current;
                    const arr = current.filter((p) => p !== fromId);
                    const insertAt = toIndex > oldIndex ? toIndex - 1 : toIndex;
                    arr.splice(insertAt, 0, fromId);
                    return arr;
                  });
                }}
                onCustomize={(id) => { setCustomizerPreviewContext('action-bar'); setCustomizerButton(id); }}
              />
            </div>

            {/* Launcher — faithful "More" grid (pinned, available, and deactivated in one surface) */}
            <div className="min-w-0">
              <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-2.5 block">
                "More" Launcher
                <span className="text-[10px] text-stone-400 ml-1 normal-case">(tap a tile to pin / unpin · greyed = deactivated)</span>
              </span>
              <MoreLauncherPreview
                theme={previewTheme}
                availableIds={activeAvailableIds.concat(deactivatedIds)}
                pinnedIds={localPinned.filter(isAssignableAction)}
                deactivatedIds={deactivatedIds}
                customizations={localCustomizations}
                onTogglePin={(id) =>
                  setLocalPinned((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
                }
                onCustomize={(id) => { setCustomizerPreviewContext('more-popup'); setCustomizerButton(id); }}
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* POS Header card — the fixed icon buttons beside the sale-screen search box */}
      {activeTab === 'header' && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5" data-testid="pos-header-editor">
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 mb-4">
            <h3 className="text-stone-800 text-sm font-semibold">POS Header</h3>
            <span className="text-xs text-stone-400">Scan, Hold and Open orders sit beside the search box on every sale screen</span>
            <div className="flex-1" />
            <PreviewThemeSwitch value={previewTheme} onChange={setPreviewTheme} saves={!!onSaveTheme} />
          </div>
          <HeaderPreview
            theme={previewTheme}
            buttons={localHeaderButtons}
            onCustomize={(id) => setHeaderCustomizer(id)}
            onMove={moveHeaderButton}
          />
        </div>
      )}

      {/* Keypad Designer card */}
      {activeTab === 'keypad' && availableIds.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 mb-3">
            <div className="flex-1" />
            {/* Preview-only theme switch — shares state with the Side Rail tab's switch */}
            <PreviewThemeSwitch value={previewTheme} onChange={setPreviewTheme} saves={!!onSaveTheme} />
          </div>
          <KeypadDesigner
            available={availableIds}
            keypadSlots={localKeypadSlots}
            buttonCustomizations={localCustomizations}
            onChange={(slots) => setLocalKeypadSlots(slots)}
            onCustomize={(actionId) => {
              setCustomizerPreviewContext('keypad');
              setCustomizerButton(actionId);
            }}
            theme={previewTheme}
          />
        </div>
      )}

      {/* Custom Item Settings card */}
      {activeTab === 'custom' && (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 mb-3">
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-accent-500" />
            <h3 className="text-stone-800 text-sm font-semibold">Custom Item Settings</h3>
          </div>
          <div className="flex-1" />
          {/* Preview-only theme switch — shares state with the other tabs' switches */}
          <PreviewThemeSwitch value={previewTheme} onChange={setPreviewTheme} saves={!!onSaveTheme} />
        </div>

        {/* Custom Item Button — this IS the keypad ADD key (one tile, follows the preview theme) */}
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Custom Item Button</span>
            <span className="text-[10px] text-stone-400 ml-1 normal-case">(the keypad ADD key — click to customize)</span>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-wrap gap-4">
            {FIXED_BUTTON_IDS.map((buttonId) => {
              const btn = BUTTON_REGISTRY[buttonId];
              if (!btn) return null;
              const cust = localCustomizations[buttonId];
              const BtnIcon = getIconFromRegistry(cust?.icon || btn.icon);
              const look = resolveAddKeyLook(cust, previewTheme);
              return (
                <button
                  key={buttonId}
                  type="button"
                  data-testid={`fixed-button-${buttonId}`}
                  onClick={() => { setCustomizerPreviewContext('keypad'); setCustomizerButton(buttonId); }}
                  className="flex flex-col items-center justify-center gap-1.5 rounded-xl select-none"
                  style={{ width: 80, height: 80, ...look.style }}
                  title="Click to customize"
                >
                  <BtnIcon size={22} color={look.fg} />
                  <span className="text-center leading-tight px-1" style={{ fontSize: 10, fontWeight: 700, color: look.fg, maxWidth: 68, wordBreak: 'break-word' }}>
                    {cust?.label || btn.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* POS Defaults: taxes and fees belong to each business, so the default
            cannot pick them. Original picker kept below, switched off. */}
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-[12px] text-stone-600 mb-1">
          Taxes and fees are set by each business — the default leaves them empty, so cashiers see all of that business's taxes and fees until it chooses.
        </div>
        {SHOW_TAX_FEE_PICKER && (<>
        <p className="text-xs text-stone-500 mb-4 leading-relaxed">
          Control which taxes and fees appear when cashiers ring up a custom (non-scanned) item.{' '}
          <strong className="text-stone-700">Show</strong> = visible in the POS popup.{' '}
          <strong className="text-stone-700">Default</strong> = pre-checked for cashier. Empty = show all.
        </p>
        {taxFeesLoading ? (
          <div className="flex items-center gap-2 text-xs text-stone-400 py-2">
            <RubyGemMark size={14} drawing />
            Loading taxes &amp; fees…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {/* TAXES */}
            <div className="border border-stone-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-200">
                <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Taxes</span>
              </div>
              <div className="p-3 space-y-2">
                {taxList.length === 0 ? (
                  <div className="text-[11px] text-stone-400 italic">No taxes configured.</div>
                ) : (
                  <>
                    <div className="grid items-center px-1" style={{ gridTemplateColumns: '1fr auto auto', gap: 10 }}>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Name</span>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Show</span>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Default</span>
                    </div>
                    {taxList.map((t) => {
                      const show = ciAvailableTaxIds.includes(t.taxFeeId);
                      const def = ciDefaultTaxIds.includes(t.taxFeeId);
                      const rate = t.rateType === 'percentage' ? `${t.rateValue}%` : `$${t.rateValue.toFixed(2)}`;
                      return (
                        <div key={t.taxFeeId} className="grid items-center px-1 py-1 rounded-lg hover:bg-stone-50" style={{ gridTemplateColumns: '1fr auto auto', gap: 10 }}>
                          <div>
                            <div className={`text-xs font-medium leading-tight ${show ? 'text-stone-700' : 'text-stone-400'}`}>{t.name}</div>
                            <div className="text-[10px] text-stone-400 tabular-nums">{rate}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleCiShow('tax', t.taxFeeId)}
                            className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 ${show ? 'bg-accent-500' : 'bg-stone-300'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${show ? 'left-[14px]' : 'left-0.5'}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleCiDefault('tax', t.taxFeeId)}
                            disabled={!show}
                            className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 ${def ? 'bg-accent-500' : 'bg-stone-300'} ${!show ? 'opacity-30 cursor-not-allowed' : ''}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${def ? 'left-[14px]' : 'left-0.5'}`} />
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>

            {/* FEES */}
            <div className="border border-stone-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-200">
                <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Fees</span>
              </div>
              <div className="p-3 space-y-2">
                {feeList.length === 0 ? (
                  <div className="text-[11px] text-stone-400 italic">No fees configured.</div>
                ) : (
                  <>
                    <div className="grid items-center px-1" style={{ gridTemplateColumns: '1fr auto auto', gap: 10 }}>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Name</span>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Show</span>
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Default</span>
                    </div>
                    {feeList.map((f) => {
                      const show = ciAvailableFeeIds.includes(f.taxFeeId);
                      const def = ciDefaultFeeIds.includes(f.taxFeeId);
                      const rate = f.rateType === 'percentage' ? `${f.rateValue}%` : `$${f.rateValue.toFixed(2)}`;
                      return (
                        <div key={f.taxFeeId} className="grid items-center px-1 py-1 rounded-lg hover:bg-stone-50" style={{ gridTemplateColumns: '1fr auto auto', gap: 10 }}>
                          <div>
                            <div className={`text-xs font-medium leading-tight ${show ? 'text-stone-700' : 'text-stone-400'}`}>{f.name}</div>
                            <div className="text-[10px] text-stone-400 tabular-nums">{rate}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleCiShow('fee', f.taxFeeId)}
                            className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 ${show ? 'bg-accent-500' : 'bg-stone-300'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${show ? 'left-[14px]' : 'left-0.5'}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleCiDefault('fee', f.taxFeeId)}
                            disabled={!show}
                            className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 ${def ? 'bg-accent-500' : 'bg-stone-300'} ${!show ? 'opacity-30 cursor-not-allowed' : ''}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${def ? 'left-[14px]' : 'left-0.5'}`} />
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        </>)}

        {/* Loyalty Points */}
        <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-2 h-2 rounded-full bg-accent-500" />
              <span className="text-sm font-semibold text-stone-700">Loyalty Points</span>
            </div>
            <p className="text-[11px] text-stone-500 ml-4">Custom items don't earn points by default. Enable to award points at the base earn rate.</p>
          </div>
          <button
            type="button"
            onClick={() => updateCiCustomItem({ loyaltyEarnsPoints: !ciLoyaltyEarnsPoints })}
            className={`relative w-10 h-6 rounded-full flex-shrink-0 transition-colors ${ciLoyaltyEarnsPoints ? 'bg-accent-500' : 'bg-stone-300'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${ciLoyaltyEarnsPoints ? 'left-[18px]' : 'left-0.5'}`} />
          </button>
        </div>
      </div>
      )}

      {/* Employee Discount Settings card */}
      {activeTab === 'empdisc' && <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-violet-500" />
            <h3 className="text-stone-800 text-sm font-semibold">Employee Discount</h3>
          </div>
        </div>
        <p className="text-xs text-stone-500 mb-3 leading-relaxed">
          When enabled, an Employee Discount button appears inside the Discount popup on this terminal.
        </p>
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-[12px] text-stone-700 mb-4">
          Employee Discount lives inside the Discount popup (not the action bar), so it is shown here as a live popup preview.
        </div>
        <div className="flex flex-wrap items-start gap-7">
          <div className="min-w-[240px] flex-1 border border-stone-200 rounded-xl p-4 space-y-4">
            {/* Enable toggle — UNCHANGED markup/handler */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-stone-700">Enable Employee Discount</div>
                <p className="text-[11px] text-stone-500 mt-0.5">Show the shortcut button inside the Discount popup</p>
              </div>
              <button
                type="button"
                data-testid="emp-discount-enable-toggle"
                onClick={() => updateEdSettings({ enabled: !edEnabled })}
                className={`relative w-10 h-6 rounded-full flex-shrink-0 transition-colors ${edEnabled ? 'bg-violet-500' : 'bg-stone-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${edEnabled ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
            {/* Percentage input — UNCHANGED markup/handler */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">Discount Percentage</label>
              <input
                type="number" min={0} max={100} step={1} value={edPercentage}
                onChange={(e) => { const v = e.target.value; updateEdSettings({ percentage: v === '' ? '' : Math.max(0, Math.min(100, Number(v))) }); }}
                disabled={!edEnabled}
                className={`w-28 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-violet-400 ${!edEnabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                placeholder="e.g. 15"
              />
            </div>
          </div>
          <div>
            <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-2 block">Live preview — Discount popup</span>
            <div className="flex flex-wrap gap-4">
              {(['light', 'dark'] as PreviewTheme[]).map((t) => (
                <div key={t}>
                  <DiscountPopupPreview enabled={edEnabled} percentage={edPercentage} theme={t} />
                  <span className="mt-1 block text-center text-[10px] font-semibold uppercase tracking-wider text-stone-400">{t === 'light' ? 'Light' : 'Dark'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>}

      {/* Customer Facing Screen tab */}
      {activeTab === 'customerfacing' && onSaveCustomerDisplay && (
        <CustomerFacingTab
          initialConfig={customerDisplayConfig ?? defaultCustomerDisplayConfig()}
          terminalType={terminalType || ''}
          onSave={onSaveCustomerDisplay}
          saving={!!savingCustomerDisplay}
          posTheme={posTheme ?? 'light'}
        />
      )}

      {/* POS Theme tab */}
      {activeTab === 'postheme' && onSaveTheme && (
        <PosThemeTab
          initialTheme={posTheme ?? 'light'}
          otherTerminalsExist={otherTerminalsExist}
          onSave={onSaveTheme}
          saving={!!savingTheme}
          variant={themeVariant === 'template' ? 'template' : 'terminal'}
        />
      )}

      {/* Shared sticky Save bar */}
      {isDirty && (
        <div className="sticky bottom-0 -mx-1 mt-2 flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg">
          <span className="text-xs text-stone-500">{themeDirty && !layoutDirty ? 'You have an unsaved POS theme change.' : 'You have unsaved layout changes.'}</span>
          <button
            onClick={handleSave}
            disabled={anySaving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-600 text-white text-sm font-semibold hover:bg-accent-700 disabled:opacity-50 transition-colors"
          >
            {anySaving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
            {saveLabel}
          </button>
        </div>
      )}

      {/* POS Header — the same Customize popup, with the header button's own defaults */}
      {headerCustomizer && (
        <ButtonCustomizerPopup
          actionId={headerCustomizer}
          registryEntry={HEADER_BUTTON_REGISTRY[headerCustomizer]}
          currentOverride={(() => {
            const { id: _id, ...o } = localHeaderButtons.find((b) => b.id === headerCustomizer) ?? { id: headerCustomizer };
            return o;
          })()}
          onApply={handleHeaderCustomizerApply}
          onClose={() => setHeaderCustomizer(null)}
          previewContext="header"
          theme={previewTheme}
        />
      )}

      {/* Button Customizer Popup */}
      {customizerButton && BUTTON_REGISTRY[customizerButton] && (
        <ButtonCustomizerPopup
          actionId={customizerButton}
          currentOverride={localCustomizations[customizerButton]}
          onApply={handleCustomizerApply}
          onClose={() => setCustomizerButton(null)}
          previewContext={customizerPreviewContext}
          theme={previewTheme}
        />
      )}
    </div>
  );
};

export default LayoutEditor;
